import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import RedditLogo from '@/assets/icons/reddit-logo.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadSelectedImagesAsZip,
  downloadImagesAsZip as downloadZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { getOptimalFontSize } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariations = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var40');

            const leftImage = new Image();
            leftImage.src = variation.leftImageSrc;
            leftImage.classList.add('left-image-var40');
            if (variation.width === 'more-width') {
              leftImage.classList.add('left-image-var40--more-width');
            }
            imageHolder.appendChild(leftImage);

            const actualPhoto = createWatermarkTagImage(
              'actual-photo-var40',
              variation.leftWatermarkTag,
              variation.leftWatermarkPosition,
            );
            if (actualPhoto) {
              imageHolder.appendChild(actualPhoto);
            }
            if (variation.divider !== 'none') {
              const middleBreak = document.createElement('div');
              middleBreak.classList.add('break-var40');
              if (variation.width === 'more-width') {
                middleBreak.classList.add('break-var40--more-width');
              }
              imageHolder.appendChild(middleBreak);
            }

            const textsHolder = document.createElement('div');
            textsHolder.classList.add('texts-holder-var40');
            if (variation.width === 'more-width') {
              textsHolder.classList.add('texts-holder-var40--more-width');
            }
            imageHolder.appendChild(textsHolder);
            const middleDivider = document.createElement('div');
            middleDivider.classList.add('middle-divider-var40');

            textsHolder.appendChild(middleDivider);
            const topTextHolder = document.createElement('div');
            topTextHolder.classList.add('top-text-holder-var40');

            const leftAuthorRow = document.createElement('div');
            leftAuthorRow.classList.add('author-row-var40');

            const leftAuthorImageEl = new Image();
            leftAuthorImageEl.classList.add('author-image-var40');

            const leftAuthorName = document.createElement('p');
            leftAuthorName.innerText = variation.leftAuthorName || '';
            leftAuthorName.classList.add('author-name-var40');

            if (variation.leftChannelNameColor === 'blue') {
              leftAuthorName.classList.add('author-name-var40__blue');
            }

            if (variation.leftChannelImageOption === 'reddit') {
              leftAuthorImageEl.src = RedditLogo;
              leftAuthorRow.appendChild(leftAuthorImageEl);
            } else {
              if (!!variation.leftAuthorImage) {
                leftAuthorImageEl.src = variation.leftAuthorImage;
                leftAuthorRow.appendChild(leftAuthorImageEl);
              }
            }

            leftAuthorRow.appendChild(leftAuthorName);

            const leftText = document.createElement('div');
            leftText.classList.add('right-text-var40'); // reuse same text class styles

            const leftTextP = document.createElement('p');

            let leftTextValue = replaceQuotes(variation.leftText || '');

            let leftAlg;

            if (variation.width === 'more-width') {
              leftAlg = getOptimalFontSize(leftTextValue, {
                maxWidth: 640,
                maxHeight: 180,
                fontFamily: 'CustomArial',
                minFontSize: 45,
                maxFontSize: 120,
              });
            } else {
              leftAlg = getOptimalFontSize(leftTextValue, {
                maxWidth: 510,
                maxHeight: 180,
                fontFamily: 'CustomArial',
                minFontSize: 45,
                maxFontSize: 120,
              });
            }

            if (variation.leftBrakes) {
              leftTextP.innerText = leftTextValue;
            } else {
              leftTextP.innerHTML = leftAlg.lines.join('<br/>');
            }

            if (variation.leftFontSize) {
              leftText.style.fontSize = `${variation.leftFontSize}px`;
            } else {
              leftText.style.fontSize = `${leftAlg.fontSize}px`;
            }

            leftText.appendChild(leftTextP);

            topTextHolder.appendChild(leftAuthorRow);
            topTextHolder.appendChild(leftText);

            const bottomTextHolder = document.createElement('div');
            bottomTextHolder.classList.add('bottom-text-holder-var40');

            const authorRow = document.createElement('div');
            authorRow.classList.add('author-row-var40');

            const authorImage = new Image();
            authorImage.classList.add('author-image-var40');

            const authorName = document.createElement('p');
            authorName.innerText = variation.authorName;
            authorName.classList.add('author-name-var40');

            if (variation.channelNameColor === 'blue') {
              authorName.classList.add('author-name-var40__blue');
            }

            if (variation.channelImageOption === 'reddit') {
              authorImage.src = RedditLogo;
              authorRow.appendChild(authorImage);
            } else {
              if (!!variation.authorImage) {
                authorImage.src = variation.authorImage;
                authorRow.appendChild(authorImage);
              }
            }

            authorRow.appendChild(authorName);

            const rightText = document.createElement('div');
            rightText.classList.add('right-text-var40');

            const rightTextP = document.createElement('p');

            let text = replaceQuotes(variation.rightText);

            let algorithmResult;

            if (variation.width === 'more-width') {
              algorithmResult = getOptimalFontSize(text, {
                maxWidth: 640,
                maxHeight: 180,
                fontFamily: 'CustomArial',
                minFontSize: 45,
                maxFontSize: 120,
              });
            } else {
              algorithmResult = getOptimalFontSize(text, {
                maxWidth: 510,
                maxHeight: 180,
                fontFamily: 'CustomArial',
                minFontSize: 45,
                maxFontSize: 120,
              });
            }

            if (variation.brakes) {
              rightTextP.innerText = text;
            } else {
              rightTextP.innerHTML = algorithmResult.lines.join('<br/>');
            }

            if (variation.fontSize) {
              rightText.style.fontSize = `${variation.fontSize}px`;
            } else {
              rightText.style.fontSize = `${algorithmResult.fontSize}px`;
            }

            rightText.appendChild(rightTextP);

            bottomTextHolder.appendChild(authorRow);

            bottomTextHolder.appendChild(rightText);

            textsHolder.appendChild(topTextHolder);
            textsHolder.appendChild(bottomTextHolder);
            imageHolder.appendChild(textsHolder);

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var40',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariations,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
